/**
 * Container component for the Download CSV modal.
 */
import { useState } from 'react';
import { getDetections } from '../../utils/api';
import { downloadCsv } from '../../utils/csv';
import { RowType } from '../../utils/detections';
import { useData } from '../../hooks/useData';
import Datepicker from '../time/Datepicker';
import ModalBasic from './ModalBasic';
import StandardButton from '../buttons/StandardButton';
import { CsvDetection } from '@/types/Detection';
import { getRegionOrder } from '@/utils/tables';
import { Column } from '@/types/Column';
import DropdownLocationSingle from '../dropdowns/DropdownLocationSingle';
import DropdownFull from '../dropdowns/DropdownFull';
import { getTimezoneOffset } from 'date-fns-tz';

export default function DownloadModalCSV({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}): JSX.Element {
  const { selectedLocation, selectedFranchise, toggled } = useData();
  const [format, setFormat] = useState('Text');
  const [selectedDates, setSelectedDates] = useState<Date[]>([
    new Date(),
    new Date(),
  ]);

  // Handle selecting a date.
  function handleSelectedDateChange(selectedDates: Date[]): void {
    setSelectedDates(selectedDates);
  }

  function convertToTimezone(timeUtc: string, timeZone?: string): string {
    // Normalize input to ISO 8601
    const [datePart, timePartWithOffset] = timeUtc.split(' ');
    const [timePart] = timePartWithOffset.split('+');
    const isoString = `${datePart}T${timePart}Z`;

    const utcDate = new Date(isoString);
    const date = new Date(utcDate.toLocaleString('en-US', { timeZone }));

    // Get local components
    const pad = (n: number, width = 2) => String(n).padStart(width, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const milliseconds = pad(date.getMilliseconds(), 3);

    // Get timezone offset in ±HH format using the location's timezone
    const offsetMs = timeZone ? getTimezoneOffset(timeZone, utcDate) : 0;
    const offsetMinutes = offsetMs / 60000;
    const offsetSign = offsetMinutes >= 0 ? '+' : '-';
    const offsetHours = pad(Math.floor(Math.abs(offsetMinutes) / 60));
    const offset = `${offsetSign}${offsetHours}`;

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}${offset}`;
  }

  function getRows(rawData: CsvDetection[]): RowType[] {
    if (rawData.length === 0) {
      return [];
    }
    const rows: RowType[] = [];
    for (const detection of rawData) {
      const row: RowType = {
        arrivalTime: detection.arrivalTime,
        arrivalTimeLocal: detection.arrivalTimeLocal,
        modelId: detection.modelId,
        totalTime: 0,
        validTimeSpent: 0
      };
      for (const region of detection.regions) {
        if (region.timeSpent === undefined) {
          region.timeSpent = 0;
        }
        row[region.key] = region.timeSpent;
      }
      row.totalTime = detection.timeSpent;
      row.validTimeSpent = detection.validTimeSpent;
      rows.push(row);
    }
    return rows;
  }

  function getColumns(rawData: CsvDetection[]): Column[] {
    if (rawData.length === 0) {
      return [];
    }
    const regions: Record<string, string> = {};
    let cols = [];
    // Get all possible regions.
    for (const data of rawData) {
      for (const region of data.regions) {
        if (region.timeSpent) {
          regions[region.key] = region.displayName;
        }
      }
    }
    for (const key in regions) {
      cols.push({
        label: regions[key],
        type: 'seconds',
        key,
      });
    }
    // sort columns
    cols = getRegionOrder(cols);
    // set first and last columns
    cols = [
      {
        label: 'Arrival Time Utc',
        key: 'arrivalTime',
        type: 'date',
      },
      {
        label: 'Arrival Time Local',
        key: 'arrivalTimeLocal',
        type: 'date',
      },
      ...cols,
      {
        label: 'Total Time',
        key: 'totalTime',
        type: 'seconds',
      },
    ];
    return cols;
  }

  async function handleDownload(): Promise<void> {
    if (selectedLocation) {
      // Fetch the data.
      const detections = await getDetections(
        selectedDates[0],
        selectedDates[1] ?? selectedDates[0], // Use selectedDates[0] twice if selectedDates[1] is undefined
        [selectedLocation.id],
        toggled,
        selectedFranchise?.id
      );

      // to CsvDetections
      const csvDetections: CsvDetection[] = detections.map((detection) => {
        return {
          ...detection,
          "arrivalTimeLocal": convertToTimezone(detection.arrivalTime, selectedLocation.timezone)
        }
      });

      const rows = getRows(csvDetections);
      let cols = getColumns(csvDetections);
      if (toggled !== 'DT') {
        cols = [...cols, {
          label: 'Valid Time',
          key: 'validTimeSpent',
          type: 'seconds',
        }];
      }

      const rowData = rows.map((row) => {
        const rowArray: any[] = [];
        for (const col of cols) {
          let rowValue = row[col.key as keyof typeof row];

          if (format.toLowerCase() == 'text') {
            if (col.key == 'arrivalTime') {
              // convert to AM / PM
              const date = new Date(rowValue);
              rowValue = date.toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              });
            } else if (col.key == 'arrivalTimeLocal') {
              const date = new Date(rowValue);
              rowValue = date.toLocaleString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                second: '2-digit',
                hour12: true,
              });
            }
            else {
              // convert to mm:ss
              const secondsNum = Number(rowValue);
              if (secondsNum && secondsNum > 0) {
                const minutes = Math.floor(secondsNum / 60);
                const seconds = Math.floor(secondsNum % 60);
                rowValue = `${minutes}:${seconds.toString().padStart(2, '0')}`;
              }
            }
          }

          rowArray.push(rowValue);
        }
        return rowArray;
      });

      console.log("cols", cols)
      // Update column if format 'text'
      if (format.toLowerCase() == 'text') {
        cols = [
          { key: 'arrivalDate', label: 'Date', type: 'date' },
          { key: 'arrivalTime', label: 'Arrival Time', type: 'date' },
          ...cols.slice(2)]
      }

      // Structure the CSV.
      downloadCsv(rowData, cols);

      // Close the modal.
      setIsOpen(false);
    }
  }

  const selectedFormat = {
    id: format,
    value: format,
  };

  return (
    <ModalBasic title="Download CSV" isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className="flex flex-col gap-4 min-w-96 p-4">
        {/* Date Selector */}
        <div className="flex flex-col gap-2">
          <div>Select Date Range</div>
          <Datepicker
            initDates={selectedDates}
            handleDateChange={handleSelectedDateChange}
          />
        </div>

        {/* Format Selector */}
        <div className="flex flex-col gap-2">
          <div>Format</div>
          <DropdownFull
            options={[{ id: "Text", value: "Text" }, { id: "Data", value: "Data" }]}
            selected={selectedFormat}
            handleChange={(id) => setFormat(id)} />
        </div>

        {/* Location Selector */}
        <div className="flex flex-col gap-2">
          <div>Select Location</div>
          <DropdownLocationSingle
          />
        </div>
        {/* Confirm Button */}
        <StandardButton
          onClick={() => {
            handleDownload().catch((error) => {
              console.error('Error downloading CSV', error);
            });
          }}
        >
          Download CSV
        </StandardButton>
      </div>
    </ModalBasic>
  );
}
